import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Loader2, Radio } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/api/client';

/**
 * The webhook that keeps tracking and refunds current.
 *
 * EasyPost pushes a message the moment a parcel is scanned or a refund settles,
 * so the Shipped list is up to date within seconds. Without it the app falls
 * back to asking every half hour, which still works - it is just slower. The
 * secret that proves a message is genuinely EasyPost's is generated here and
 * never shown to anyone.
 *
 * A webhook belongs to the key that created it: a test key's webhook only
 * hears about test shipments, and a production key needs its own. Register
 * again after changing to the production key.
 */
export default function EasyPostWebhookSection() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['easypostWebhook'],
    queryFn: () => api.request('/easypost/webhook'),
  });

  const onDone = (message) => (status) => {
    queryClient.setQueryData(['easypostWebhook'], status);
    toast.success(message);
  };

  const register = useMutation({
    mutationFn: () => api.request('/easypost/webhook', { method: 'POST' }),
    onSuccess: onDone('Webhook registered with EasyPost'),
    onError: (error) => toast.error(error.message ?? 'Could not register the webhook'),
  });

  const remove = useMutation({
    mutationFn: () => api.request('/easypost/webhook', { method: 'DELETE' }),
    onSuccess: onDone('Webhook removed. Tracking will be checked every half hour instead.'),
    onError: (error) => toast.error(error.message ?? 'Could not remove the webhook'),
  });

  const busy = register.isPending || remove.isPending;

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-slate-100 to-slate-50">
        <CardTitle className="flex items-center gap-2">
          <Radio className="w-5 h-5" />
          EasyPost webhook
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6 space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              Lets EasyPost tell the app straight away when a parcel is scanned, delivered, or a
              refund is settled. Without it, tracking is still checked every half hour.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              {data?.registered ? (
                <Badge className="bg-green-600 gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Registered
                </Badge>
              ) : data?.disabled ? (
                <Badge variant="destructive">Switched off by EasyPost</Badge>
              ) : (
                <Badge variant="secondary">Not registered</Badge>
              )}
              {data?.mode && data.mode !== 'unknown' && (
                <span className="text-xs text-slate-500">
                  {data.mode === 'test' ? 'Test key: hears about test shipments only' : 'Production key'}
                </span>
              )}
            </div>

            <p className="text-xs text-slate-500 break-all">
              Address EasyPost sends to: <span className="font-mono">{data?.endpoint}</span>
            </p>

            {!data?.keyConfigured && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  Add the EasyPost API key above first.
                </AlertDescription>
              </Alert>
            )}

            {data?.keyConfigured && data?.problem && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">{data.problem}</AlertDescription>
              </Alert>
            )}

            {data?.disabled && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  EasyPost stopped sending after repeated failures. Register again to switch it back
                  on.
                </AlertDescription>
              </Alert>
            )}

            {data?.listError && (
              <p className="text-xs text-red-700">Could not ask EasyPost: {data.listError}</p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                disabled={busy || !data?.keyConfigured || Boolean(data?.problem)}
                onClick={() => register.mutate()}
              >
                {register.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {data?.registered || data?.disabled ? 'Register again' : 'Register webhook'}
              </Button>

              {(data?.registered || data?.disabled) && (
                <Button variant="outline" disabled={busy} onClick={() => remove.mutate()}>
                  {remove.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Remove
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
